import { useUserStore, useTabbarStore } from '@/store'
import { useLoginStore } from '@/store/modules/login'

// tabbar 页面列表
const tabbarPages = [
  '/pages/index/index',
  '/pages/activity/index',
  '/pages/game/game',
  '/pages/recommend/index',
  '/pages/mine/mine',
]

// 白名单页面，不需要登录就能访问
const whiteList = [
  '/pages/index/index',
  '/pages/activity/index',
  '/pages/game/game',
  '/pages/recommend/index',
  '/pages/mine/mine',
  '/pages-sub/common/service',
  '/pages-sub/game/gameList',
]

// 检查页面是否在白名单中
const isInWhiteList = (url: string): boolean => {
  return whiteList.some((path) => url.startsWith(path))
}

// 检查是否是 tabbar 页面
const isTabbarPage = (url: string): boolean => {
  // 去除查询参数再判断
  const path = url.split('?')[0]
  return tabbarPages.some((tabPath) => path === tabPath)
}

// 将对象转换为查询字符串
const objectToQueryString = (obj: Record<string, any>): string => {
  if (!obj || Object.keys(obj).length === 0) return ''

  const params = new URLSearchParams()
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // 处理数组
      if (Array.isArray(value)) {
        value.forEach((item) => params.append(key, String(item)))
      }
      // 处理对象
      else if (typeof value === 'object') {
        params.append(key, JSON.stringify(value))
      }
      // 处理基本类型
      else {
        params.append(key, String(value))
      }
    }
  })

  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

// 获取完整的页面路径
const getFullPath = (url: string, query?: Record<string, any>): string => {
  // 处理基础路径
  const basePath = url.startsWith('/') ? url : `/pages/${url}`

  // 如果没有 query 参数，直接返回基础路径
  if (!query) return basePath

  // 将现有的 URL 参数和新的 query 参数合并
  const [path, existingQuery] = basePath.split('?')
  const existingParams = new URLSearchParams(existingQuery || '')
  const newParams = new URLSearchParams(objectToQueryString(query).replace('?', ''))

  // 合并参数
  newParams.forEach((value, key) => {
    existingParams.append(key, value)
  })

  const finalQueryString = existingParams.toString()
  return finalQueryString ? `${path}?${finalQueryString}` : path
}

interface NavigateOptions extends Omit<UniNamespace.NavigateToOptions, 'url'> {
  url: string
  needLogin?: boolean // 是否需要登录
  type?: 'navigateTo' | 'redirectTo' | 'reLaunch' | 'switchTab' // 跳转类型
  query?: Record<string, any> // 查询参数
}

/**
 * 统一的页面跳转方法
 * @param options 跳转参数
 * @returns Promise
 */
export const navigate = (options: NavigateOptions | string): Promise<any> => {
  // 统一处理参数
  const opt: NavigateOptions = typeof options === 'string' ? { url: options } : options
  const fullPath = getFullPath(opt.url, opt.query)

  // 设置默认值
  opt.type = opt.type || 'navigateTo'
  opt.needLogin = opt.needLogin ?? !isInWhiteList(fullPath)
  opt.url = fullPath

  return new Promise((resolve, reject) => {
    // 检查是否需要登录
    if (opt.needLogin) {
      const userStore = useUserStore()
      const loginStore = useLoginStore()

      if (!userStore.isLogined) {
        // 保存当前页面路径，登录后跳转
        loginStore.showLogin(opt.url)
        reject(new Error('请先登录'))
        return
      }
    }

    // 根据类型调用对应的跳转方法
    const navigator = {
      navigateTo: uni.navigateTo,
      redirectTo: uni.redirectTo,
      reLaunch: uni.reLaunch,
      switchTab: uni.switchTab,
    }[opt.type]

    navigator({
      ...opt,
      success: resolve,
      fail: reject,
    })
  })
}

// 快捷方法
export const navigateTo = (options: NavigateOptions | string) => {
  const opt: NavigateOptions = typeof options === 'string' ? { url: options } : options
  const fullPath = getFullPath(opt.url, opt.query)
  // 如果是 tabbar 页面，自动使用 switchTab
  console.log(fullPath)

  if (isTabbarPage(fullPath)) {
    const tabbarStore = useTabbarStore()
    // 获取当前页面在 tabbar 中的索引
    const index = tabbarPages.indexOf(fullPath)
    tabbarStore.setCurrIndex(index)
    return
    // return navigate({
    //   ...opt,
    //   type: 'switchTab',
    //   url: fullPath.split('?')[0], // switchTab 不支持参数，需要去除
    // })
  }

  return navigate({
    ...opt,
    type: 'navigateTo',
  })
}

export const redirectTo = (options: NavigateOptions | string) => {
  return navigate({
    ...(typeof options === 'string' ? { url: options } : options),
    type: 'redirectTo',
  })
}

export const reLaunch = (options: NavigateOptions | string) => {
  return navigate({
    ...(typeof options === 'string' ? { url: options } : options),
    type: 'reLaunch',
  })
}

export const switchTab = (options: NavigateOptions | string) => {
  const opt: NavigateOptions = typeof options === 'string' ? { url: options } : options
  return navigate({
    ...opt,
    type: 'switchTab',
    url: getFullPath(opt.url).split('?')[0], // switchTab 不支持参数，需要去除
  })
}

export const navigateBack = () => {
  // 判断是否还有上一页
  const pages = getCurrentPages()
  if (pages.length > 1) {
    uni.navigateBack()
  } else {
    uni.switchTab({
      url: '/pages/index/index',
    })
  }
}
